import React from "react";
import { useField } from "formik";
import { emptyToNull } from "@common/helpers/string";
import { GridCol } from "@design-system/Grid/GridCol";
import { GridRow } from "@design-system/Grid/GridRow";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { InputSize, toDsInputSize } from "@helpers/form";
import { CountrySelect } from "./CountrySelect";
import { StateSelect } from "./StateSelect";
import styles from "./AddressForm.module.scss";

interface Props {
    prefix?: string;
    includeCountry?: boolean;
    includeStreet2?: boolean;
    wide?: boolean;
    fieldSize?: InputSize;
    readOnly?: boolean;
}

export const AddressForm: React.FC<Props> = ({
    prefix = "address",
    includeCountry,
    includeStreet2,
    wide,
    fieldSize,
    readOnly,
}) => {
    const finalPrefix = prefix.length ? `${prefix}.` : "";
    const inputSize = toDsInputSize(fieldSize);
    const stateButtonSize = fieldSize === "sm" ? "xl" : "lg";

    const [countryField] = useField<string | null | undefined>(
        `${finalPrefix}country`,
    );
    const showState = !includeCountry || countryField.value === "US";

    if (wide && includeStreet2) {
        throw new Error("Cannot include street2 with wide form");
    }

    return (
        <>
            {includeCountry ? (
                <FormikField<string | null>
                    name={`${finalPrefix}country`}
                    label="Country"
                    control="button"
                    className={styles.selectField}
                >
                    {({ field, helpers, invalid }) => (
                        <CountrySelect
                            name={field.name}
                            value={emptyToNull(field.value)}
                            onValueChange={(country) => {
                                void helpers.setValue(country);
                                void helpers.setTouched(true);
                            }}
                            invalid={invalid}
                        />
                    )}
                </FormikField>
            ) : null}

            {!wide || !showState ? (
                <>
                    <FormikField
                        name={`${finalPrefix}street`}
                        label={includeStreet2 ? "Street line 1" : "Street"}
                        size={inputSize}
                        showErrorMessage={!readOnly}
                        className={styles.textField}
                    />

                    {includeStreet2 && (
                        <FormikField
                            name={`${finalPrefix}street2`}
                            label="Street line 2"
                            size={inputSize}
                            showErrorMessage={!readOnly}
                            className={styles.textField}
                        />
                    )}
                </>
            ) : null}

            {showState ? (
                <>
                    <GridRow>
                        {wide ? (
                            <GridCol sm={6}>
                                <FormikField
                                    name={`${finalPrefix}street`}
                                    label="Street"
                                    size={inputSize}
                                    showErrorMessage={!readOnly}
                                    className={styles.textField}
                                />
                            </GridCol>
                        ) : null}
                        <GridCol sm={wide ? 6 : 12}>
                            <FormikField
                                name={`${finalPrefix}city`}
                                label="City"
                                size={inputSize}
                                showErrorMessage={!readOnly}
                                className={styles.textField}
                            />
                        </GridCol>
                    </GridRow>
                    <GridRow>
                        <GridCol sm={wide ? 6 : 8}>
                            <FormikField<string>
                                name={`${finalPrefix}state`}
                                label="State"
                                control="button"
                                showErrorMessage={!readOnly}
                                className={styles.selectField}
                            >
                                {({ field, helpers, invalid }) => (
                                    <StateSelect
                                        name={field.name}
                                        value={field.value || null}
                                        onValueChange={(state) =>
                                            void helpers.setValue(state ?? "")
                                        }
                                        invalid={invalid}
                                        buttonSize={stateButtonSize}
                                    />
                                )}
                            </FormikField>
                        </GridCol>
                        <GridCol sm={wide ? 6 : 4}>
                            <FormikField
                                name={`${finalPrefix}postalCode`}
                                label="ZIP"
                                size={inputSize}
                                showErrorMessage={!readOnly}
                                className={styles.textField}
                            />
                        </GridCol>
                    </GridRow>
                </>
            ) : (
                <GridRow>
                    <GridCol sm={wide ? 6 : 8}>
                        <FormikField
                            name={`${finalPrefix}city`}
                            label="City"
                            size={inputSize}
                            className={styles.textField}
                        />
                    </GridCol>
                    <GridCol sm={wide ? 6 : 4}>
                        <FormikField
                            name={`${finalPrefix}postalCode`}
                            label="ZIP"
                            size={inputSize}
                            className={styles.textField}
                        />
                    </GridCol>
                </GridRow>
            )}
        </>
    );
};
